/**
 * Response schemas for the staff/user profile photo (`BACKLOG.md` A12, direct instruction
 * 2026-08-18). Same self-service-plus-admin shape as `signatureRequests.ts`, and the same reason
 * this file exists: the wire shapes both `server/` and `web/` validate against, kept in one place so
 * they cannot drift.
 *
 * **No request-body schema here, unlike `signatureRequests.ts`/`localeRequests.ts`.** A photo upload
 * is a file, not JSON — `POST /api/profile/photo` and its admin counterpart both accept
 * `multipart/form-data` with a single `photo` field, so there is no JSON shape for a Zod schema to
 * validate client-side; `server/src/documents/storage.ts`'s own `verifyMagicBytes`/malware-scan
 * pipeline is what actually validates the uploaded bytes, server-side, after the fact. Both `GET`
 * responses and the upload's own success response share `profilePhotoResponseSchema`/
 * `memberProfilePhotoResponseSchema` below.
 *
 * **A staff photo is not PHI, and is deliberately treated more simply than a patient photo (`C18`)
 * would be** — see `server/src/db/schema/platform.ts`'s `users.photoStorageRef` column comment and
 * `server/src/auth/profilePhotoRoutes.ts`'s own header for the full argument. Nothing here carries a
 * confidentiality label or an audit-on-read requirement the way a clinical photo would.
 */

import { z } from 'zod';

/**
 * A headshot, not a high-resolution upload — 5 MiB comfortably covers a phone photo straight off a
 * modern camera (typically 2-4 MB) with headroom, while still bounding the request body before it
 * ever reaches `documentStorage.uploadDocument`. Enforced server-side
 * (`profilePhotoRoutes.ts`) before the bytes are read into memory at all.
 */
export const MAX_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024;

/** `GET`/`POST`/`DELETE /api/profile/photo` — the caller's own current photo. `null` means no photo
 * on file, not an error. The URL is a time-limited presigned link
 * (`server/src/documents/storage.ts`'s `getDocumentUrl`), never a raw/public storage URL, and a
 * caller must not cache it past its own short lifetime. */
export const profilePhotoResponseSchema = z.strictObject({
  photoUrl: z.string().nullable(),
});
export type ProfilePhotoResponse = z.infer<typeof profilePhotoResponseSchema>;

/** `GET`/`POST`/`DELETE /api/orgs/members/:userId/photo` — the admin surface, naming which member
 * the photo belongs to since an admin is acting on someone else's account. */
export const memberProfilePhotoResponseSchema = z.strictObject({
  userId: z.uuid(),
  photoUrl: z.string().nullable(),
});
export type MemberProfilePhotoResponse = z.infer<typeof memberProfilePhotoResponseSchema>;

/**
 * `unsupported_file_type` and `malware_detected` map to `server/src/documents/storage.ts`'s
 * `DocumentContentMismatchError`/`DocumentMalwareDetectedError` respectively — named distinctly
 * rather than folded into the generic `invalid_request`, since "this isn't really a JPEG/PNG" and
 * "the scanner flagged this file" are different, actionable situations for the uploader to see (even
 * though, per that module's own header, no upload has ever actually been scanned for malware —
 * `malware_detected` is real plumbing for a stub that never fires it today). `file_too_large` is this
 * route's own check, ahead of either.
 */
export const PROFILE_PHOTO_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'unsupported_file_type',
  'file_too_large',
  'malware_detected',
] as const;
export type ProfilePhotoErrorCode = (typeof PROFILE_PHOTO_ERROR_CODES)[number];
